import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { AuthGuard } from '@/components/auth/AuthGuard';
import { Shell } from '@/components/layout/Shell';
import { useTheme } from '@/hooks/useTheme';
import { useMixtapeFontsLoader } from '@/lib/mixtapeFonts';

// Public routes — Login and AuthCallback are tiny and on the critical path
// for sign-in. Worth keeping in the initial bundle so the magic-link return
// doesn't wait on a chunk fetch.
import Login from '@/routes/Login';
import AuthCallback from '@/routes/AuthCallback';

// Authenticated routes are lazy-loaded so the first paint after login only
// downloads what's needed for the current page. Each route becomes its own
// chunk in the build output.
const Dashboard  = lazy(() => import('@/routes/Dashboard'));
const Users      = lazy(() => import('@/routes/Users'));
const Tapes      = lazy(() => import('@/routes/Tapes'));
const Mixtapes   = lazy(() => import('@/routes/Mixtapes'));
const Broadcasts = lazy(() => import('@/routes/Broadcasts'));
const Genres     = lazy(() => import('@/routes/Genres'));
const Watch      = lazy(() => import('@/routes/Watch'));
const Portada    = lazy(() => import('@/routes/Portada'));
const Search     = lazy(() => import('@/routes/Search'));
const Fonts      = lazy(() => import('@/routes/Fonts'));
const FeedPreview = lazy(() => import('@/routes/FeedPreview'));
const Ops        = lazy(() => import('@/routes/Ops'));
const Audit      = lazy(() => import('@/routes/Audit'));
const Analytics  = lazy(() => import('@/routes/Analytics'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 10_000,
    },
  },
});

// Minimal fallback while a route chunk loads. Voice-aligned ("Tuning…"
// matches the rest of the loading states across the UI).
function RouteFallback() {
  return (
    <div className="flex h-full items-center justify-center text-fg-faint text-sm">
      Tuning…
    </div>
  );
}

// Sibling component so the runtime <link> injection happens inside the
// QueryClientProvider context (the loader hook reads the cached fonts
// query). Renders nothing.
function MixtapeFontsLoader() {
  useMixtapeFontsLoader();
  return null;
}

export default function App() {
  const { theme } = useTheme();
  return (
    <QueryClientProvider client={queryClient}>
      <MixtapeFontsLoader />
      <Toaster
        position="bottom-right"
        theme={theme}
        toastOptions={{
          // Match the backoffice's tokens so toasts feel native rather than
          // tacked-on. Sonner takes inline style overrides per toast type
          // via the `style` map below isn't supported in v2; we use the
          // CSS-variable hook instead.
          classNames: {
            toast:        'rounded-md border border-border bg-bg-card text-fg shadow-editorial',
            title:        'font-sans text-sm',
            description:  'text-xs text-fg-muted',
            actionButton: 'bg-thermal-orange text-bg',
            cancelButton: 'bg-bg-elevated text-fg',
            error:        'border-thermal-red/40',
            success:      'border-status-success/40',
          },
        }}
      />
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Routes>
          {/* Public */}
          <Route path="/login" element={<Login />} />
          {/* Magic-link return URL. The component waits for Supabase to
              consume the URL hash (detectSessionInUrl is async) and only
              then redirects home / Navigating synchronously here would clear
              the hash before supabase-js reads it. */}
          <Route path="/auth/callback" element={<AuthCallback />} />

          {/* Authenticated / any signed-in user can see Shell. Per-view role
              gating is done inside each route as needed. */}
          <Route
            element={
              <AuthGuard>
                <Suspense fallback={<RouteFallback />}>
                  <Shell />
                </Suspense>
              </AuthGuard>
            }
          >
            <Route index               element={<Dashboard />} />
            <Route path="users"        element={<Users />} />
            <Route path="tapes"        element={<Tapes />} />
            <Route path="mixtapes"     element={<Mixtapes />} />
            <Route path="broadcasts"   element={<Broadcasts />} />
            <Route path="genres"       element={<Genres />} />
            <Route path="fonts"        element={<Fonts />} />
            <Route path="watch"        element={<Watch />} />
            <Route path="portada"      element={<Portada />} />
            <Route path="search"       element={<Search />} />
            <Route path="preview"      element={<FeedPreview />} />
            <Route path="ops"          element={<Ops />} />
            <Route path="audit"        element={<Audit />} />
            <Route path="analytics"    element={<Analytics />} />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
